import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText } from "lucide-react";
import Seo from "../components/Seo";
import Reveal from "../components/Reveal";
import { OnRequestBox, planSlug, roleIcon } from "../components/PlanParts";
import { PLANS } from "../data/mock";

// Pagina dedicata a un listino: /listini/spartano, /listini/perfezionista, ...
const Listino = () => {
  const { slug } = useParams();
  const plan = PLANS.find((p) => planSlug(p) === slug);

  if (!plan) return <Navigate to="/#offerte" replace />;

  return (
    <>
      <Seo
        title={`Listino ${plan.name} – ${plan.tagline}`}
        description={`${plan.subtitle} Scopri i componenti del listino ${plan.name} di Energeide: moduli, inverter e accumulo.`}
        path={`/listini/${slug}`}
      />

      {/* HERO scuro: identita' a sinistra, cosa include + CTA a destra */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A1F44] to-[#0d2855] text-white">
        <div
          aria-hidden
          className="absolute -top-24 -right-24 w-80 h-80 bg-[#F4C542] rounded-full blur-[120px] opacity-15"
        />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <Link
            to="/#offerte"
            className="inline-flex items-center gap-2 text-sm font-montserrat font-semibold text-gray-300 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Tutte le offerte
          </Link>

          <div className="grid lg:grid-cols-[1fr,380px] gap-8 lg:gap-14 items-start">
            <Reveal>
              {plan.badge && (
                <span className="inline-block px-2.5 py-1 mb-4 rounded-md bg-[#F4C542] text-[#0A1F44] text-[10px] font-montserrat font-bold tracking-[0.22em] uppercase">
                  {plan.badge}
                </span>
              )}
              <h1 className="text-5xl sm:text-7xl font-montserrat font-extrabold leading-none mb-3">
                {plan.name}
              </h1>
              <p className="text-sm font-montserrat font-semibold tracking-[0.1em] uppercase text-[#F4C542]">
                {plan.tagline}
              </p>
              <p className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed max-w-xl">
                {plan.subtitle}
              </p>
            </Reveal>

            <Reveal delay={120} className="bg-white text-[#0A1F44] rounded-2xl p-6 shadow-2xl">
              <p className="text-[11px] font-montserrat font-bold tracking-[0.2em] uppercase text-gray-500 mb-4">
                Cosa include
              </p>
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700">
                    <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-[#0FB36B]/15 shrink-0">
                      <Check className="w-3.5 h-3.5 text-[#0FB36B]" />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to="/contatti"
                className="flex items-center justify-center gap-2 rounded-xl h-12 bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] text-sm font-montserrat font-bold transition-colors"
              >
                {plan.ctaLabel || "Richiedi preventivo"}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMPONENTI: una riga per componente */}
      <section className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-montserrat font-bold text-[#0A1F44] mb-8">
            {plan.onRequest ? "Configurazione" : "Cosa trovi nel listino"}
          </h2>

          {plan.onRequest ? (
            <OnRequestBox onRequest={plan.onRequest} />
          ) : (
            <div className="divide-y divide-gray-200 border-y border-gray-200">
              {plan.components.map((c, i) => {
                const Icon = roleIcon(c.role);
                return (
                  <Reveal
                    key={`${plan.name}-${c.role}`}
                    delay={i * 80}
                    className="grid md:grid-cols-[220px,1fr,auto] gap-4 md:gap-8 items-center py-6"
                  >
                    <div className="flex items-center gap-3">
                      <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-[#0A1F44] text-[#F4C542] shrink-0">
                        <Icon className="w-6 h-6" />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[11px] font-montserrat font-bold tracking-[0.18em] uppercase text-gray-500">
                          {c.role}
                        </p>
                        <p className="text-xl font-montserrat font-extrabold text-[#0A1F44] leading-tight">
                          {c.brand}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">{c.model}</p>
                      </div>
                    </div>

                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
                      {c.specs.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#F4C542] mt-1.5 shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>

                    {c.pdf ? (
                      <a
                        href={c.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-4 h-11 rounded-lg border border-[#0A1F44]/15 text-xs font-montserrat font-bold text-[#0A1F44] hover:bg-[#F4C542] hover:border-[#F4C542] transition-colors whitespace-nowrap"
                      >
                        <FileText className="w-4 h-4" />
                        Scheda tecnica
                      </a>
                    ) : (
                      <span />
                    )}
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ALTRI LISTINI */}
      <section className="bg-gray-50 border-t border-gray-100 py-10 sm:py-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <p className="text-[11px] font-montserrat font-bold tracking-[0.2em] uppercase text-gray-500 mb-4">
            Confronta con gli altri listini
          </p>
          <div className="flex flex-wrap gap-3">
            {PLANS.filter((p) => p !== plan).map((o) => (
              <Link
                key={o.name}
                to={`/listini/${planSlug(o)}`}
                className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-white border border-gray-200 text-sm font-montserrat font-bold text-[#0A1F44] hover:border-[#F4C542] hover:shadow-md transition-all"
              >
                {o.name}
                <ArrowRight className="w-4 h-4" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Listino;
